/**
 * Login Page Component
 * 
 * Provides user authentication form with username/email and password.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import { useI18n } from '../i18n';
import './Auth.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
      const destination = location.state?.from;
      navigate(destination ? `${destination.pathname}${destination.search || ''}${destination.hash || ''}` : '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || t('auth.login.failure'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ThemeToggle className="auth-theme-toggle" />
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">⚡</span>
            <h1>{t('app.dashboardName')}</h1>
          </div>
          <p className="auth-subtitle">{t('auth.login.subtitle')}</p>
          {process.env.REACT_APP_DESKTOP === 'true' && (
            <p>Use your spikescope.org account. Connect for your first sign-in on this computer; afterward you can sign in and work offline. Recordings stay on this computer.</p>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">{t('auth.login.usernameLabel')}</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.login.usernamePlaceholder')}
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.login.passwordLabel')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.login.passwordPlaceholder')}
              required
              autoComplete="current-password"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="button-spinner"></span>
                {t('auth.login.submitting')}
              </>
            ) : (
              t('auth.login.submit')
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="auth-link">
              {t('auth.login.createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
